import type { ISpDemographicStep, PatchSpDemographicsInput, SpDemographicStepKind } from '@brushfire/types'
import dayjs from 'dayjs'
import {
  isValidUsMobileNumber,
  normalizeUsMobileForStorage,
} from '@/features/account/lib/account-phone-utils'

export type StepFormValues = Record<string, string>

export const SP_MIN_AGE_ERROR = 'You must be at least 18 years old to participate.'

export function isAtLeast18(dateOfBirth: string): boolean {
  if (!dateOfBirth || !dayjs(dateOfBirth).isValid()) return false
  return dayjs().diff(dayjs(dateOfBirth), 'year') >= 18
}

/** Linear-time email shape check — avoids ReDoS-prone regex. */
function isValidEmail(raw: string): boolean {
  const at = raw.indexOf('@')
  if (at < 1) return false
  const domain = raw.slice(at + 1)
  if (!domain || domain.includes('@')) return false
  const dot = domain.indexOf('.')
  return dot > 0 && dot < domain.length - 1
}

export function buildPatchPayload(
  step: ISpDemographicStep,
  values: StepFormValues,
  options?: { omitNameFields?: boolean },
): PatchSpDemographicsInput {
  const payload: PatchSpDemographicsInput = {}
  for (const field of step.fields) {
    if (options?.omitNameFields && (field === 'firstName' || field === 'lastName')) {
      continue
    }
    applyFieldToPayload(field, values, payload)
  }
  return payload
}

function applyFieldToPayload(
  field: string,
  values: StepFormValues,
  payload: PatchSpDemographicsInput,
): void {
  if (field === 'state' || field === 'city') {
    payload.state = values.state
    payload.city = values.city
    return
  }
  if (field === 'age') {
    if (values.dateOfBirth) payload.dateOfBirth = values.dateOfBirth
    if (values.gender) payload.gender = values.gender
    return
  }
  if (field === 'email' && values.email) {
    payload.email = values.email.trim()
    return
  }
  if (field === 'phone' && values.phone) {
    payload.phone = normalizeUsMobileForStorage(values.phone)
    return
  }
  if (field in values && values[field]) {
    ;(payload as Record<string, string>)[field] = values[field]
  }
}

export function validateStep(step: ISpDemographicStep, values: StepFormValues): string | null {
  const byKind: Partial<Record<SpDemographicStepKind, () => string | null>> = {
    personalInfo: () => {
      if (step.fields.includes('firstName') && !values.firstName?.trim()) {
        return 'This field is required'
      }
      if (step.fields.includes('lastName') && !values.lastName?.trim()) {
        return 'This field is required'
      }
      return null
    },
    email: () => {
      const raw = values.email?.trim()
      if (!raw) return 'This field is required'
      if (!isValidEmail(raw)) return 'Enter a valid email address'
      return null
    },
    phone: () => {
      const raw = values.phone?.trim()
      if (!raw) return 'This field is required'
      if (!isValidUsMobileNumber(raw)) return 'Enter a valid US phone number'
      return null
    },
    identity: () => {
      if (step.fields.includes('age')) {
        if (!values.dateOfBirth) return 'This field is required'
        if (!isAtLeast18(values.dateOfBirth)) return SP_MIN_AGE_ERROR
      }
      if (step.fields.includes('gender') && !values.gender) return 'This field is required'
      return null
    },
    location: () => {
      if (!values.state) return 'This field is required'
      if (!values.city?.trim()) return 'This field is required'
      return null
    },
    householdSize: () => (values.householdSize ? null : 'This field is required'),
    numberOfChildren: () =>
      values.numberOfChildren ? null : STEP_ERROR_FOR_KIND('numberOfChildren'),
    industry: () => (values.industry ? null : STEP_ERROR_FOR_KIND('industry')),
    maritalStatus: () => (values.maritalStatus ? null : STEP_ERROR_FOR_KIND('maritalStatus')),
    housingStatus: () => (values.housingStatus ? null : STEP_ERROR_FOR_KIND('housingStatus')),
    education: () => (values.education ? null : 'This field is required'),
    employment: () => (values.employment ? null : 'This field is required'),
  }

  const validate = byKind[step.kind]
  if (validate) return validate()

  const field = step.fields[0]
  if (!field) return null
  return values[field] ? null : STEP_ERROR_FOR_KIND(step.kind)
}

function STEP_ERROR_FOR_KIND(kind: SpDemographicStepKind): string {
  switch (kind) {
    case 'numberOfChildren':
      return 'Please select number of children'
    case 'industry':
      return 'Please select your industry or field of work'
    case 'maritalStatus':
      return 'Please select your marital status'
    case 'housingStatus':
      return 'Please select your housing status'
    default:
      return 'This field is required'
  }
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`
}
